import type { Client } from '../../api/client.js';
import type { Modele, PersonneEquipe } from './dependances.js';

export interface FicheRapide {
  nom: string;
  sousTitre: string;
  appeler: string | null;
  whatsapp: string | null;
  email: string | null;
}

type Contact = PersonneEquipe['contacts'][number];

/** Contact du type demandé, le préféré d'abord : l'équipe voit toutes les coordonnées, même masquées à la famille. */
const contactDe = (personne: PersonneEquipe, type: Contact['type']): Contact | undefined =>
  [...personne.contacts].filter((c) => c.type === type).sort((a, b) => Number(b.isPreferred) - Number(a.isPreferred))[0];

export function construireFiche(personne: PersonneEquipe): FicheRapide {
  const prenom = personne.firstNames.trim().split(/\s+/)[0] ?? '';
  const tel = contactDe(personne, 'phone');
  const wa = contactDe(personne, 'whatsapp');
  const mail = contactDe(personne, 'email');
  return {
    nom: [prenom, personne.lastName].filter(Boolean).join(' '),
    sousTitre: personne.relationLabel ?? '',
    appeler: tel ? `tel:${tel.value}` : null,
    whatsapp: wa ? `https://wa.me/${wa.value.replace(/\D/g, '')}` : null,
    email: mail ? `mailto:${mail.value}` : null,
  };
}

/** Modèles validés par Isabelle et destinés à la famille (un brouillon ne part jamais). */
export const modelesEnvoyables = (modeles: readonly Modele[]): Modele[] =>
  modeles.filter((m) => m.status === 'valide_isabelle' && m.recipient === 'famille');

export type VerdictEnvoi = { ok: true } | { ok: false; message: string };

const MESSAGE_MANDANT = 'Ce message nomme la mandante. Retirez son nom avant l’envoi.';
const MESSAGE_VARIABLES = 'Ce message contient des champs à compléter entre accolades. Complétez-les avant l’envoi.';
export const MESSAGE_NOMS_INCONNUS =
  'Impossible de vérifier les noms à protéger pour le moment. Rechargez la page, puis réessayez : aucun message ne part sans cette vérification.';
const normaliser = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/**
 * RG-02 : le mandant n'est jamais nommé dans un message destiné aux autres héritiers. Garde fermée par défaut : tant que les
 * noms du mandant ne sont pas connus (`null` : lecture en cours ou échouée), rien ne part.
 */
export function verifierEnvoi(corps: string, nomsMandant: readonly string[] | null): VerdictEnvoi {
  if (nomsMandant === null) return { ok: false, message: MESSAGE_NOMS_INCONNUS };
  const texte = normaliser(corps);
  const nomme = nomsMandant.map((n) => normaliser(n.trim())).some((n) => n.length > 0 && texte.includes(n));
  if (nomme) return { ok: false, message: MESSAGE_MANDANT };
  if (/\{[a-z_]+\}/i.test(corps)) return { ok: false, message: MESSAGE_VARIABLES };
  return { ok: true };
}

export async function envoyerModele(
  client: Client,
  entree: {
    personId: string;
    dossierId: string;
    channelAccountId: string;
    modele: Modele;
    corps: string;
    nomsMandant: readonly string[] | null;
  },
): Promise<VerdictEnvoi> {
  const verdict = verifierEnvoi(entree.corps, entree.nomsMandant);
  if (!verdict.ok) return verdict;
  await client.appeler('messagerie.envoyer', {
    corps: {
      channelAccountId: entree.channelAccountId,
      personId: entree.personId,
      dossierId: entree.dossierId,
      templateCode: entree.modele.code,
      body: entree.corps,
      scheduledAt: null,
      attachmentIds: [],
    },
  });
  return { ok: true };
}
